import { useParams } from "react-router-dom";
import axiosInstance from "../api";
import { Button, Checkbox, Col, DatePicker, Input, Row, Spin, Table, Tree, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { ApiOutlined, LinkOutlined, LoadingOutlined, SyncOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import PermissionRequired from "../common/PermissionRequired";
import { withTooltip } from "../util";
const { Text } = Typography;

export default function OrderAccountingReport() {
    const { orderId } = useParams();
    const [report, setReport] = useState({});
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [searchText, setSearchText] = useState();
    const [refIdSearchText, setRefIdSearchText] = useState();
    const [updatingChecked, setUpdatingChecked] = useState(false);
    const [range, setRange] = useState();

    const fetchData = () => {
        setLoading(true);
        axiosInstance
            .get(`/order/${orderId}/accountingReport`)
            .then(({ data }) => setReport(data))
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        fetchData();
    }, []);

    const filteredLines = useMemo(
        () =>
            (report.lines ?? []).filter(
                (x) =>
                    (!searchText ||
                        x.section?.name.toLowerCase().includes(searchText.toLowerCase())) &&
                    (!refIdSearchText ||
                        ((x.contractItem?.refId ?? "EXTRA") + x.description)
                            .toLowerCase()
                            .includes(refIdSearchText.toLowerCase())) &&
                    (!range ||
                        (dayjs(x.accounting.month).isSameOrAfter(range[0]) &&
                            dayjs(x.accounting.month).isSameOrBefore(range[1]))),
            ),
        [report, searchText, refIdSearchText, range],
    );

    useEffect(() => {
        const data = filteredLines;

        const m2 = data
            .map((x) => ({
                ...x,
                key: x.id,
                title: (
                    <Row>
                        <Col flex={"700px"}>
                            {x.supplyOfId && <ApiOutlined />}{" "}
                            {withTooltip(
                                <Text ellipsis style={{ width: 550 }}>
                                    {x.contractItem?.refId ?? "EXTRA"} {x.description}
                                </Text>,
                                (x.contractItem?.refId ?? "EXTRA") + " " + x.description,
                            )}
                            <Button
                                type="link"
                                icon={<LinkOutlined />}
                                size="small"
                                onClick={() => {
                                    window.open(
                                        `/orders/${orderId}/accountings/${x.accounting.id}`,
                                        "_blank",
                                    );
                                }}
                            />
                        </Col>
                        <Col flex={"auto"}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "end",
                                }}
                            >
                                <span style={{ width: 150, textAlign: "end" }}>
                                    {dayjs(x.accounting.month).format("MM-YYYY")}
                                </span>
                                <span style={{ width: 150, textAlign: "end" }}>
                                    {x.quantity.toLocaleString("it-IT", {
                                        minimumFractionDigits: 2,
                                    })}
                                </span>
                                {report.hasPrices && (
                                    <>
                                        <span style={{ width: 150, textAlign: "end" }}>
                                            {x.unitPrice.toLocaleString("it-IT", {
                                                minimumFractionDigits: 2,
                                            })}
                                        </span>
                                        <span style={{ width: 150, textAlign: "end" }}>
                                            {x.totalPrice.toLocaleString("it-IT", {
                                                minimumFractionDigits: 2,
                                            })}
                                        </span>
                                    </>
                                )}

                                <PermissionRequired name={"accountingItem.checked"}>
                                    <span style={{ width: 50, textAlign: "end" }}>
                                        <Checkbox
                                            checked={x.checked}
                                            onChange={(e) => updateChecked(x.id, e.target.checked)}
                                        />
                                    </span>
                                </PermissionRequired>
                            </div>
                        </Col>
                    </Row>
                ),
            }))
            .map((x) => ({ k: x.supplyOfId ?? x.id, v: x }))
            .reduce(
                (acc, val) => ({
                    ...acc,
                    [val.k]: [...(acc[val.k] ?? []), val.v],
                }),
                {},
            );

        const m3 = Object.values(m2)
            .map((x) => ({
                ...x.filter((x) => !x.supplyOfId)[0],
                children: x.filter((x) => !!x.supplyOfId),
            }))
            .map((x) => ({ ...x, children: x.children.length > 0 && x.children }));
        const mapped = m3.map((x) => ({
            k: x.section ?? { id: -1, name: "NESSUNA TRATTA" },
            v: x,
        }));
        const res = mapped.reduce(
            (acc, val) => ({
                ...acc,
                [val.k.id]: {
                    id: `s_${val.k.id}`,
                    key: `s_${val.k.id}`,
                    title: <b>{val.k.name}</b>,
                    isSectionRow: true,
                    name: val.k.name,
                    children: [...(acc[val.k.id]?.children ?? []), val.v],
                },
            }),
            {},
        );
        setTableData(Object.values(res));
    }, [report, filteredLines]);

    const keys = () =>
        report.lines && [
            ...new Set([
                ...report.lines?.map((x) => x.id),
                ...report.lines?.map((x) => `s_${x.section?.id ?? -1}`),
            ]),
        ];

    const updateChecked = (itemId, value) => {
        setUpdatingChecked(true);
        axiosInstance.put(`accountingItem/${itemId}/checked`, { checked: value }).then(() => {
            setReport({
                ...report,
                lines: report.lines.map((x) => (x.id === itemId ? { ...x, checked: value } : x)),
            });
            setUpdatingChecked(false);
        });
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                rowGap: 16,
                pointerEvents: updatingChecked ? "none" : "auto",
            }}
        >
            <Spin spinning={loading}>
                <Row>
                    <Col flex={"auto"}>
                        <div style={{ display: "flex", flexDirection: "row", columnGap: 16 }}>
                            <Input
                                allowClear
                                value={searchText}
                                onChange={({ target }) => setSearchText(target.value)}
                                style={{ width: 200 }}
                                placeholder={"Filtra tratta"}
                            />
                            <Input
                                allowClear
                                value={refIdSearchText}
                                onChange={({ target }) => setRefIdSearchText(target.value)}
                                style={{ width: 200 }}
                                placeholder={"Filtra voce"}
                            />
                            <DatePicker.RangePicker
                                picker="month"
                                format="MM/YYYY"
                                onChange={(x) =>
                                    setRange(x && [x[0].startOf("month"), x[1].endOf("month")])
                                }
                                value={range}
                            />
                        </div>
                    </Col>
                    <Col flex={"50px"}>
                        <Button type="link" icon={<SyncOutlined spin={loading}/>} onClick={fetchData}>Aggiorna</Button>
                    </Col>
                </Row>

                <Row style={{ marginTop: 20 }}>
                    <Col flex={"700px"}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                            }}
                        >
                            <span style={{ width: 70, fontWeight: "bold" }}>Tratta</span>
                            <span style={{ width: 150, fontWeight: "bold" }}>Articolo</span>
                        </div>
                    </Col>
                    <Col flex={"auto"}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "end",
                            }}
                        >
                            <span style={{ width: 150, fontWeight: "bold", textAlign: "end" }}>
                                Competenza
                            </span>
                            <span style={{ width: 150, fontWeight: "bold", textAlign: "end" }}>
                                Quantità
                            </span>
                            {report.hasPrices && (
                                <>
                                    <span
                                        style={{ width: 150, fontWeight: "bold", textAlign: "end" }}
                                    >
                                        Importo unitario
                                    </span>
                                    <span
                                        style={{ width: 150, fontWeight: "bold", textAlign: "end" }}
                                    >
                                        Importo totale
                                    </span>
                                </>
                            )}

                            <PermissionRequired name={"accountingItem.checked"}>
                                <span style={{ width: 50, textAlign: "end" }}>
                                    <Spin
                                        spinning={updatingChecked}
                                        size="small"
                                        indicator={<LoadingOutlined spin />}
                                    />
                                </span>
                            </PermissionRequired>
                        </div>
                    </Col>
                </Row>
                <Tree showLine expandedKeys={keys()} treeData={tableData} height={700}></Tree>
                <Row style={{ marginTop: 20 }}>
                    <Col flex={"800px"}></Col>
                    <Col flex={"auto"}>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "end",
                            }}
                        >
                            <span style={{ width: 150, fontWeight: "bold", textAlign: "end" }}>
                                Totale
                            </span>

                            <span style={{ width: 150, textAlign: "end" }}>
                                {filteredLines
                                    .reduce((acc, curr) => acc + curr.quantity, 0)
                                    .toLocaleString("it-IT", {
                                        minimumFractionDigits: 2,
                                    })}
                            </span>
                            <PermissionRequired name="global.viewPrices">
                                <span style={{ width: 150, textAlign: "end" }}></span>
                                <span style={{ width: 150, textAlign: "end" }}>
                                    {report.summary?.totalPrice.toLocaleString("it-IT", {
                                        minimumFractionDigits: 2,
                                    })}
                                </span>
                            </PermissionRequired>

                            <PermissionRequired name={"accountingItem.checked"}>
                                <span style={{ width: 50 }}></span>
                            </PermissionRequired>
                        </div>
                    </Col>
                </Row>
            </Spin>
        </div>
    );
}
